import { useMemo, useRef } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, FormBuilder, Text } from '@kula-ai/components';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { ClarifyFormBlockData, ClarifyFormField } from '../../../types';
import { buildSubmitMessage } from './buildSubmitMessage';
import type { FieldValue } from './fields';
import { buildDefaults, buildFormSchema, seedLabels } from './formSchema';

interface ClarifyFormBlockProps {
  data: ClarifyFormBlockData;
}

interface FormHandle {
  submit: () => void;
}

// Pre-flight form the agent emits before it runs (propose_clarify_form). The
// fields are backend-authored, so the whole thing is config-driven: the action
// list maps onto FormBuilder's schema, which owns rendering, labels, and
// required-field validation.
export const ClarifyFormBlock = ({ data }: ClarifyFormBlockProps) => {
  const { send, isSending } = useChatSubmit();
  const isStreaming = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return false;
    const tab = s.openTabs.find((t) => t.sessionId === sid);
    return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
  });
  // Already answered (hydrated transcript): show it, but lock it.
  const consumed = !!data.consumed;
  const disabled = isSending || isStreaming || consumed;

  const formRef = useRef<FormHandle>(null);
  const labels = useMemo(() => seedLabels(data.actions), [data.actions]);
  const schema = useMemo(
    () => buildFormSchema(data.actions, labels),
    [data.actions, labels]
  );
  const defaults = useMemo(() => buildDefaults(data.actions), [data.actions]);

  // Human substitution for the visible half of the message — pickers show
  // their label(s), text fields the raw value. The machine values the agent
  // needs travel separately in the <ctx:…> marker.
  const substitution = (field: ClarifyFormField, value: FieldValue): string => {
    if (Array.isArray(value)) {
      if (value.length === 0) return 'none';
      return value.map((v) => labels.get(v) ?? v).join(', ');
    }
    if (field.type === 'picker') return labels.get(value) ?? value;
    return value;
  };

  const onSubmit = (values: Record<string, unknown>) => {
    if (disabled) return;
    const typed = values as Record<string, FieldValue>;
    void send(buildSubmitMessage(data, typed, substitution));
  };

  return (
    <Box
      data-test-id="agentforge-clarify-form-block"
      bg="base-white"
      border="1px solid"
      borderColor="border-color"
      borderRadius="12px"
      overflow="visible"
      boxShadow="0 1px 3px rgba(16, 24, 40, 0.06)"
      w="100%"
    >
      <Box
        px="16px"
        py="12px"
        borderBottom="1px solid"
        borderColor="border-color"
        bg="secondary-25"
        borderTopRadius="12px"
      >
        <Text
          variant="span"
          className="text-[14px] text-secondary-900 font-semibold"
        >
          {data.intro}
        </Text>
      </Box>

      <Box px="16px" py="16px">
        <FormBuilder
          ref={formRef}
          schema={schema}
          defaultValues={defaults}
          onSubmit={onSubmit}
          disabled={disabled}
          readOnly={consumed}
          columns={1}
          errorDisplay="onSubmit"
        />
      </Box>

      <Flex
        px="16px"
        py="12px"
        borderTop="1px solid"
        borderColor="border-color"
        bg="secondary-25"
        align="center"
        justify="space-between"
        gap="12px"
        borderBottomRadius="12px"
      >
        <Text variant="small" className="text-secondary-500">
          {consumed ? 'Submitted.' : 'Review the details, then create.'}
        </Text>
        {!consumed && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => formRef.current?.submit()}
            isDisabled={disabled}
            isLoading={isSending}
          >
            {data.submitLabel}
          </Button>
        )}
      </Flex>
    </Box>
  );
};
